import React, { useState, useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from '../../../../Event/event';

const FunMathQuizGame = () => {
  const [score, setScore] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [feedbackColor, setFeedbackColor] = useState('');
  const [confettiElements, setConfettiElements] = useState([]);
  const [answerValue, setAnswerValue] = useState('');
  const inputRef = useRef(null);

  const questions = [
    { q: "Convert 1/2 to a decimal", a: 0.5 },
    { q: "Convert 1/4 to a decimal", a: 0.25 },
    { q: "Convert 3/4 to a decimal", a: 0.75 },
    { q: "Convert 1/5 to a decimal", a: 0.2 },
    { q: "Convert 2/5 to a decimal", a: 0.4 },
    { q: "Convert 1/3 to a decimal (round to 2 digits)", a: 0.33 },
    { q: "Convert 2/3 to a decimal (round to 2 digits)", a: 0.67 },
    { q: "What is 10% of 90?", a: 9 },
    { q: "What is 25% of 80?", a: 20 },
    { q: "What is 50% of 48?", a: 24 },
    { q: "What is 33% of 60 (approximate)?", a: 20 },
    { q: "If a toy costs $40 and is on 25% discount, what is the discount amount?", a: 10 },
    { q: "A $60 item has 10% tax. What’s the final price?", a: 66 },
    { q: "You buy something for $45 and sell it for $60. What is your profit?", a: 15 },
    { q: "An item bought at $80 was sold at $60. What is the loss?", a: 20 },
    { q: "Estimate: 99 + 51 ≈ ?", a: 150 },
    { q: "Estimate: 199 + 315 = ?", a: 514 },
    { q: "Estimate the total bill: $23.75 + $18.20", a: 42 },
    { q: "Estimate change: You pay $50 for a $37 item", a: 13 },
    { q: "Is 17 a prime or composite number?", a: "prime" },
    { q: "Is 24 a prime or composite number?", a: "composite" },
    { q: "List all prime numbers between 10 and 20 (comma-separated)", a: "11,13,17,19" },
    { q: "What is the smallest prime number?", a: 2 },
    { q: "Is 1 prime or composite?", a: "neither" },
    { q: "What is the opposite of -7?", a: 7 },
    { q: "Which is smaller: -4 or 0?", a: "-4" },
    { q: "What is -3 + 5?", a: 2 },
    { q: "What is -10 + (-5)?", a: -15 },
    { q: "What is the absolute value of -8?", a: 8 },
    { q: "Which number is greater: -9 or -3?", a: -3 }
  ];

  const normalizeAnswer = (input) =>
    input.trim().toLowerCase().replace(/\s+/g, '');

  const checkAnswer = (e) => {
    e.preventDefault();
    const userAnswerRaw = answerValue;
    const correctAnswer = questions[currentQuestionIndex].a;
    let isCorrect = false;

    if (typeof correctAnswer === 'number') {
      isCorrect = Number(userAnswerRaw) === correctAnswer;
    } else {
      isCorrect =
        normalizeAnswer(userAnswerRaw) === normalizeAnswer(correctAnswer);
    }

    if (isCorrect) {
      setScore((prev) => prev + 1);
      setFeedback('🎉 Correct!');
      setFeedbackColor('#2e7d32');
      showConfetti();
    } else {
      setFeedback(`❌ Wrong! The correct answer is: ${correctAnswer}`);
      setFeedbackColor('#c62828');
    }

    setAnswerValue(''); // Clear immediately

    setTimeout(() => {
      setCurrentQuestionIndex((prev) => prev + 1);
      setFeedback('');
    }, 2000);
  };

  const showConfetti = () => {
    const elements = [];
    for (let i = 0; i < 30; i++) {
      elements.push(
        <div
          key={i + Math.random()}
          style={{
            position: 'fixed',
            width: '10px',
            height: '10px',
            backgroundColor: `hsl(${Math.floor(
              Math.random() * 360
            )}, 70%, 60%)`,
            opacity: 0.8,
            pointerEvents: 'none',
            animation: `fall ${2 + Math.random() * 2}s linear forwards`,
            left: Math.random() * window.innerWidth + 'px'
          }}
        ></div>
      );
    }
    setConfettiElements(elements);
    setTimeout(() => setConfettiElements([]), 3000);
  };

  // Clear & focus when question changes
  useEffect(() => {
    setAnswerValue('');
    inputRef.current?.focus();
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (currentQuestionIndex === questions.length) {
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log('Event completed', responsedata);
    }
  }, [currentQuestionIndex]);

  const question = questions[currentQuestionIndex];

  return (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundImage:
          'url("https://static.vecteezy.com/system/resources/thumbnails/021/826/876/small_2x/hand-drawn-math-symbols-icon-in-flat-style-mathematics-formula-illustration-on-isolated-background-school-education-sign-business-concept-vector.jpg")',
        minHeight: '100vh',
        overflowX: 'hidden'
      }}
    >
      {confettiElements}
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100vh',
          padding: '20px'
        }}
      >
        <div
          style={{
            background: '#fff',
            border: '3px solid #4a148c',
            borderRadius: '30px',
            padding: '40px 30px 50px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }}
        >
          <h2
            style={{
              fontSize: '34px',
              color: '#4a148c',
              fontFamily: 'Comic Neue, cursive',
              textShadow: '1px 1px 3px #b39ddb'
            }}
          >
            Score → <span>{score}</span>
          </h2>
          <h3
            style={{
              fontSize: '22px',
              margin: '20px 0 30px',
              minHeight: '3em'
            }}
          >
            {question
              ? question.q
              : `🎉 Quiz Complete! Your final score is ${score} / ${questions.length}`}
          </h3>
          {question && (
            <form
              onSubmit={checkAnswer}
              style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}
            >
              <input
                ref={inputRef}
                name="answer"
                type="text"
                required
                autoComplete="off"
                inputMode="numeric"
                value={answerValue}
                onChange={(e) => setAnswerValue(e.target.value)}
                style={{
                  fontSize: '22px',
                  padding: '15px',
                  border: '2px solid #4a148c',
                  borderRadius: '15px',
                  textAlign: 'center'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '1rem' }}>
  <button
    type="button"
    onClick={() => {
      setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
      setFeedback('');
    }}
    style={{
      flex: 1,
      padding: '1rem',
      backgroundColor: '#6a1b9a',
      color: '#fff',
      fontSize: '1rem',
      fontWeight: 700,
      border: 'none',
      borderRadius: '15px',
      cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
      opacity: currentQuestionIndex === 0 ? 0.5 : 1,
    }}
    disabled={currentQuestionIndex === 0}
  >
    Previous
  </button>

  <button
    type="button"
    onClick={() => {
      setCurrentQuestionIndex((prev) =>
        Math.min(prev + 1, questions.length - 1)
      );
      setFeedback('');
    }}
    style={{
      flex: 1,
      padding: '1rem',
      backgroundColor: '#4a148c',
      color: '#fff',
      fontSize: '1rem',
      fontWeight: 700,
      border: 'none',
      borderRadius: '15px',
      cursor:
        currentQuestionIndex === questions.length - 1 ? 'not-allowed' : 'pointer',
      opacity: currentQuestionIndex === questions.length - 1 ? 0.5 : 1,
    }}
    disabled={currentQuestionIndex === questions.length - 1}
  >
    Next
  </button>
</div>

              <button
                type="submit"
                style={{
                  padding: '15px',
                  backgroundColor: '#4a148c',
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: '15px',
                  cursor: 'pointer'
                }}
              >
                Submit
              </button>
            </form>
          )}
          <div
            style={{
              fontSize: '20px',
              marginTop: '15px',
              height: '30px',
              fontWeight: 700,
              fontFamily: 'Comic Neue, cursive',
              color: feedbackColor
            }}
          >
            {feedback}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(600px) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default FunMathQuizGame;
